<script>
/**
 * A linha ÚNICA do raciocínio (11/09/2026) — o indicador de fase do turno,
 * no molde do Claude.ai: `✳ Consultando cliente...  8s ⌄`.
 *
 * Substituiu DOIS elementos que se empilhavam na mesma bolha: o card
 * "Raciocínio da IA" (`reasoning-panel`, rótulo fixo + lista sempre à vista) e
 * o rótulo de fase do rodapé (`chat-message-footer` → `rotulo-do-turno`).
 *
 * | estado | linha | à direita |
 * |---|---|---|
 * | turno rodando | o PASSO ATUAL (`passoAtual`, de `utils/rotulo-do-turno.js`) com o shimmer de varredura no texto; passo novo SUBSTITUI o anterior (`iv2-passo`, fade `out-in` de 260ms + 3px de deslize, sem mudar a altura — a `__caixa` segura) | contador `Ns` (cronômetro próprio: começa quando `streaming` liga, para quando desliga) + chevron |
 * | concluído, turno LONGO | **[T064]** `rotuloDoTempo` — "Pensando por 1 min 12 s", o fecho que a bolha monta (`chat-message.passo.js`); é o cabeçalho colapsado do ChatGPT/Claude | só "7 etapas": a duração sai do auxiliar para não ser dita duas vezes na mesma linha |
 * | concluído, turno CURTO (< 3 s) ou histórico sem `duracao_s` | o título FIXO `RACIOCINIO_LINHA.titulo` ("Raciocínio da IA") — sem shimmer: é a forma que diz que acabou (o último passo parado lia como travado) | o resumo `RACIOCINIO_LINHA.resumo` ("7 etapas · 4,2s": etapas DEPOIS de agrupar; `duracaoSegundos` da mensagem ou o cronômetro, com uma casa; sem duração, só "N etapas") em `az-ds-aux` + chevron |
 * | erro | a mensagem em `--danger-text` | chevron |
 *
 * Expandida (clique, `aria-expanded`): a lista dos passos — o que o
 * `reasoning-panel` mostrava (racional da triage + "Consultando X..." dos
 * tool-calls) —, cada um uma linha discreta com um `border-left` hairline, sem
 * card; o verbo apagado e o objeto escuro ("Consultei" · "o funil de leads",
 * separados por `utils/raciocinio-passos.js`), o `meta` muted ao lado. Aberta
 * ou fechada, a escolha PERSISTE enquanto o turno roda; passo novo entra na
 * lista com a MESMA animação dos cartões (`iv2-parte-*`).
 *
 * Polish de 14/09/2026 (prints do usuário): `mb-3` no root (a linha ficava
 * colada no texto da resposta — o pai não tem `gap`), `az-gap-xsmall` entre
 * os passos, e o cabeçalho concluído ganhou um REALCE de hover na tipografia:
 * o texto escurece (`--neutral-700` → `--neutral-900`) e recebe UM passe do
 * gradiente da varredura do passo atual (`iv2-varredura-de-texto` com
 * iteração 1, `efeitos.sass`) — sem loop, sem fundo, sem pílula; o chevron
 * desceu 1px para centrar na x-height do texto.
 *
 * Um indicador por bolha: enquanto o turno roda, esta linha é o ÚNICO
 * elemento animado — texto em streaming e cartões já entregues ficam
 * estáticos; o "Estruturando próximos passos..." (bloco `next-steps`) e o
 * "Montando a resposta..." (`cartao-pendente`) saíram por isso.
 *
 * Acessibilidade: o passo atual é anunciado por um `<output aria-live>`
 * visualmente oculto — `role="status"` no elemento visível seria região viva
 * num nó que troca por transição (o Vue remonta o `<span>` no `out-in`).
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila).
 */
import IndicadorDeAtividade, {
  MODOS_DO_INDICADOR,
} from '@modules/aawz-intelligence-v2/components/indicador-de-atividade/indicador-de-atividade.vue'
import { RACIOCINIO_LINHA } from '@modules/aawz-intelligence-v2/constants/TextChat'
import { agruparPassos } from '@modules/aawz-intelligence-v2/utils/raciocinio-passos'
import { formatNumber } from '@utils/Format'

const UM_SEGUNDO_MS = 1000
// Quanto tempo o genérico "Pensando..." fica no PULSO antes de virar ÓRBITA
// (owner, 21/09/2026: *"o primeiro segundo fica a bolinha pulsando e depois a
// animação"*). Passado isso a órbita só diz "está trabalhando" — o texto segue
// "Pensando...", que é a verdade: entre o `start` e a primeira tool o backend
// não emite nada (é a latência do modelo), e o Supervisor manda `node-status`
// que o front ainda ignora (`apiChat._onNodeStatus`).
const SEGUNDOS_DE_PULSO_ANTES_DA_ORBITA = 1
// O sinal de atividade é o ponto em `--primary` com três modos
// (`components/indicador-de-atividade/`, owner 21/09/2026) — substituiu o
// asterisco `ai-sparkle` de 16px; a caixa dele tem os MESMOS 16px, então
// trocar de modo nunca move o texto ao lado.
const TAMANHO_DO_CHEVRON = 14
const ICONE_DO_CHEVRON = 'arrow-down-new-ux'
// Casas decimais da duração no resumo ("4,2s").
const CASAS_DA_DURACAO = 1

export default {
  name: 'IntelligenceV2RaciocinioLinha',
  components: { IndicadorDeAtividade },
  props: {
    // Os passos, na ordem: racional da triage + um por evento `thinking`
    // (ao vivo) ou `message.thinking_steps` (persistida).
    passos: {
      type: Array,
      default: function () {
        return []
      },
    },
    // O que o turno está fazendo AGORA (`rotuloDoTurno`). Só lido em streaming.
    passoAtual: {
      type: String,
      default: '',
    },
    streaming: {
      type: Boolean,
      default: false,
    },
    // O passo atual é uma ETAPA de verdade (tool, fase) — e não o genérico
    // "Pensando..."? Quem sabe é a bolha (`passoPrendeATela`, em
    // `chat-message.passo.js`, que conhece o `textoDePreparo` da persona); a
    // linha só escolhe o MODO do sinal: órbita para a etapa, pulso para o
    // genérico (owner, 21/09/2026).
    passoDeTrabalho: {
      type: Boolean,
      default: false,
    },
    // Duração persistida na mensagem (`duracao_s`, gravado por
    // `apiChat._onEventEnd`); `null` no histórico antigo — aí a linha
    // concluída mostra só o último passo.
    duracaoSegundos: {
      type: Number,
      default: null,
    },
    // ★ [spec-008 T064] "Pensando por 1 min 12 s" — o fecho do turno, montado
    // pela bolha (`chat-message.passo.js::rotuloDeTempoDoTurno`, que é quem
    // tem o limiar). Vazio em streaming, no turno curto e no histórico sem
    // `duracao_s`; vazio ⇒ a linha concluída volta ao título fixo de sempre.
    rotuloDoTempo: {
      type: String,
      default: '',
    },
    // O motivo de um turno que morreu — a linha vira erro, ainda expansível.
    erro: {
      type: String,
      default: '',
    },
    // `{ [rótulo do passo]: 'texto' }` — o auxiliar barato de cada passo
    // ("1.667 linhas", o nome do cliente), calculado pela bolha a partir das
    // partes do turno. Passo sem entrada não ganha auxiliar.
    auxiliares: {
      type: Object,
      default: function () {
        return {}
      },
    },
  },
  data() {
    return {
      aberta: false,
      // O cronômetro: segundos desde que `streaming` ligou nesta instância.
      segundos: 0,
      temporizador: null,
      TAMANHO_DO_CHEVRON,
      ICONE_DO_CHEVRON,
    }
  },
  computed: {
    temErro() {
      return Boolean(this.erro)
    },
    // Rodando de verdade: o shimmer e o cronômetro seguem isto, nunca o
    // `streaming` cru quando o turno já morreu.
    processando() {
      return this.streaming && !this.temErro
    },
    ultimoPasso() {
      const lista = this.passos || []
      return lista.length ? lista[lista.length - 1] : ''
    },
    // Os segundos que a linha mostra: os da mensagem (persistida, com uma
    // casa) vencem o cronômetro (inteiros); sem nenhum dos dois, nada.
    segundosExibidos() {
      if (typeof this.duracaoSegundos === 'number') return this.duracaoSegundos
      return this.segundos
    },
    concluido() {
      return !this.processando && !this.temErro
    },
    // Os passos prontos para a lista: repetidos consecutivos agrupados, em
    // PASSADO depois do `end`, já com `verbo` (apagado) + `complemento`
    // (escuro) e o `meta` muted ("15× · 1.667 linhas") — o template só imprime.
    grupos() {
      return agruparPassos(this.passos, {
        auxiliares: this.auxiliares,
        passado: this.concluido,
      })
    },
    etapas() {
      return this.grupos.length
    },
    // Colapsada e concluída: o título FIXO ("Raciocínio da IA") — o resumo
    // ("7 etapas · 4,2s") vai para o auxiliar ao lado. O último passo parado
    // ("Elaborando resposta") lia como travado; o que diz "acabou" é a forma.
    texto() {
      if (this.temErro) return this.erro
      if (this.processando) return this.passoAtual || this.ultimoPasso
      // ★ [T064] O turno que fez o consultor esperar DIZ quanto esperou —
      // é o cabeçalho colapsado do ChatGPT/Claude ("Thought for 8 seconds").
      // Turno curto não ganha rótulo e cai no título fixo de sempre.
      return this.rotuloDoTempo || RACIOCINIO_LINHA.titulo
    },
    // Enquanto roda: o cronômetro (`7s`); concluída: "N etapas · X,Xs".
    auxiliarDaLinha() {
      if (this.temErro) return ''
      if (this.processando) {
        return this.segundosExibidos > 0 ? RACIOCINIO_LINHA.segundos(this.segundosExibidos) : ''
      }
      if (!this.etapas) return ''
      // ★ [T064] Com o rótulo de tempo no título, a duração SAI do auxiliar:
      // "Pensando por 42 s · 7 etapas · 42,0s" diria o mesmo número duas
      // vezes na mesma linha — e um ponto focal por linha é o que a faz ler.
      const duracao =
        !this.rotuloDoTempo && this.segundosExibidos > 0
          ? formatNumber(this.segundosExibidos, CASAS_DA_DURACAO)
          : ''
      return RACIOCINIO_LINHA.resumo(this.etapas, duracao)
    },
    temPassos() {
      return this.etapas > 0
    },
    // Só expande o que tem lista para mostrar.
    expansivel() {
      return this.temPassos
    },
    mostraLista() {
      return this.aberta && this.temPassos
    },
    // `--concluido` é o gatilho do realce de hover: só a linha parada
    // reage ao mouse — em streaming o texto já varre sozinho, e dois brilhos
    // sobrepostos leem como bug.
    classesDoBotao() {
      return {
        'raciocinio-linha__botao--expansivel': this.expansivel,
        'raciocinio-linha__botao--concluido': this.concluido,
      }
    },
    classesDoTexto() {
      return {
        'iv2-shimmer-varredura': this.processando,
        'raciocinio-linha__texto--erro': this.temErro,
      }
    },
    corDoIcone() {
      if (this.temErro) return 'var(--danger-text)'
      return 'var(--primary)'
    },
    /**
     * Os três modos do sinal (owner, 21/09/2026): turno PARADO (concluído,
     * histórico, erro) é o ponto quieto; o genérico "Pensando..." é o ponto
     * pulsando no PRIMEIRO segundo e a órbita dali em diante; uma etapa de
     * verdade (tool, fase) é a órbita na hora — "fazendo mais coisas além de
     * pensar". O cronômetro que decide é o mesmo do contador `Ns`.
     */
    modoDoIndicador() {
      if (!this.processando) return MODOS_DO_INDICADOR.ESTATICO
      if (this.passoDeTrabalho) return MODOS_DO_INDICADOR.ORBITA
      const passouDoPulso = this.segundos >= SEGUNDOS_DE_PULSO_ANTES_DA_ORBITA
      return passouDoPulso ? MODOS_DO_INDICADOR.ORBITA : MODOS_DO_INDICADOR.PULSO
    },
    ariaExpanded() {
      return this.aberta ? 'true' : 'false'
    },
    ariaLista() {
      return RACIOCINIO_LINHA.ariaLista
    },
  },
  watch: {
    processando: {
      immediate: true,
      handler(ligado) {
        if (ligado) {
          this.iniciarCronometro()
          return
        }
        this.pararCronometro()
      },
    },
  },
  beforeDestroy() {
    this.pararCronometro()
  },
  methods: {
    alternar() {
      if (!this.expansivel) return
      this.aberta = !this.aberta
    },
    iniciarCronometro() {
      if (this.temporizador !== null) return
      this.segundos = 0
      const self = this
      this.temporizador = setInterval(function () {
        self.segundos += 1
      }, UM_SEGUNDO_MS)
    },
    // Para SEM zerar: o número que ficou é a duração medida, e é ele que a
    // linha concluída mostra quando a mensagem não trouxe `duracao_s`.
    pararCronometro() {
      if (this.temporizador === null) return
      clearInterval(this.temporizador)
      this.temporizador = null
    },
  },
}
</script>

<template>
  <!-- `mb-3`: o respiro contra o que vem abaixo (o texto da resposta ou a
       mesa) mora AQUI porque o `assistant-blocks` não tem `gap` e o
       `.ia-block-card` só ganha `margin-top` depois de `.ia-message-content`;
       a lista expandida está dentro deste root, então o mesmo 12px separa o
       último passo do conteúdo. -->
  <div class="raciocinio-linha mb-3" data-teste="raciocinio-linha">
    <!-- Tudo colado à ESQUERDA, como na referência (`✳ Descobrindo…  8s ⌄`):
         ícone → texto → auxiliar → chevron, sem `ml-auto`/`justify-space-between`.
         O texto e o auxiliar dividem um `inline-flex` alinhado pela BASELINE
         (o `align-center` do botão desceria o auxiliar, que é menor).
         `inline-flex` (não `d-flex` full-width): o hover só acende sobre o
         que é clicável, não sobre a linha inteira. -->
    <button
      type="button"
      class="raciocinio-linha__botao d-inline-flex align-center text-left pa-0"
      :class="classesDoBotao"
      :aria-expanded="ariaExpanded"
      :disabled="!expansivel"
      data-teste="raciocinio-linha-botao"
      @click="alternar"
    >
      <IndicadorDeAtividade
        class="raciocinio-linha__icone mr-2"
        :modo="modoDoIndicador"
        :cor="corDoIcone"
        data-teste="raciocinio-linha-indicador"
      />
      <span class="raciocinio-linha__conteudo d-inline-flex align-baseline">
        <!-- A caixa segura a altura durante o `out-in`: entre o passo que sai
             e o que entra não há `<span>` nenhum no DOM. -->
        <span class="raciocinio-linha__caixa">
          <transition name="iv2-passo" mode="out-in">
            <span
              :key="texto"
              class="raciocinio-linha__texto az-text-primary az-size-14"
              :class="classesDoTexto"
              data-teste="raciocinio-linha-texto"
            >{{ texto }}</span>
          </transition>
        </span>
        <span
          v-if="auxiliarDaLinha"
          class="raciocinio-linha__auxiliar az-ds-aux flex-shrink-0 ml-2"
          data-teste="raciocinio-linha-auxiliar"
        >{{ auxiliarDaLinha }}</span>
      </span>
      <icon-svg
        v-if="expansivel"
        class="raciocinio-linha__chevron flex-shrink-0 ml-1"
        :class="{ 'raciocinio-linha__chevron--aberto': aberta }"
        :name="ICONE_DO_CHEVRON"
        :size="TAMANHO_DO_CHEVRON"
        color="var(--neutral-700)"
      />
    </button>

    <!-- Região viva SÓ para o leitor de tela: o texto visível troca por
         transição (o nó é remontado), e `aria-live` nele anunciaria pela
         metade. -->
    <output class="raciocinio-linha__anuncio" aria-live="polite">{{ texto }}</output>

    <!-- Expandida: lista indentada com um filete à esquerda, sem card — um
         passo por linha (só `az-gap-xsmall` entre eles — `py-1` deixava os passos longe, 14/09), o verbo apagado
         e o objeto escuro, e o `meta` muted ("15× · 1.667 linhas") ao lado.
         O recuo é do `<style>` (`$lado-do-icone`): o texto alinha sob o título
         (ícone + `mr-2`) e o filete cai sob o CENTRO do ícone — com o ícone de
         22px não há utilitário Vuetify para 11px/30px. `mr-0` no `li`: o global
         `.content-nav ul li { margin-right: 18px }` alcança esta lista.
         `<ul is="transition-group">` em vez de `<transition-group tag="ul">`:
         é o MESMO vnode (o `transition-group` lê o `tag` do `is`), mas o
         `<li>` fica literalmente dentro de um `<ul>` no template (Sonar
         Web:ItemTagNotWithinContainerTagCheck). -->
    <ul
      is="transition-group"
      v-if="mostraLista"
      name="iv2-parte"
      class="raciocinio-linha__lista az-gap-xsmall mt-1 mb-0"
      :aria-label="ariaLista"
      data-teste="raciocinio-linha-lista"
    >
      <li
        v-for="item in grupos"
        :key="item.chave"
        class="raciocinio-linha__passo d-flex align-baseline py-0 mr-0"
      >
        <!-- O espaço depois do verbo é um caractere de verdade dentro do
             span (não `mr-1`): o leitor de tela e o copiar/colar leem
             "Consultei o cliente", não "Consulteio cliente". Fica DENTRO do
             span porque o compilador (`whitespace: condense`) descarta o
             whitespace com quebra de linha entre as duas tags. -->
        <span class="raciocinio-linha__passo-texto ia-texto-raciocinio-passo">
          <span
            v-if="item.verbo"
            class="raciocinio-linha__passo-verbo"
            data-teste="raciocinio-linha-passo-verbo"
          >{{ item.verbo }} </span>
          <span
            v-if="item.complemento"
            class="raciocinio-linha__passo-complemento"
            data-teste="raciocinio-linha-passo-complemento"
          >{{ item.complemento }}</span>
        </span>
        <span
          v-if="item.meta"
          class="az-ds-aux ml-2 flex-shrink-0"
          data-teste="raciocinio-linha-passo-meta"
        >· {{ item.meta }}</span>
      </li>
    </ul>
  </div>
</template>

<style lang="sass" scoped>
// A varredura do passo atual, a troca do passo (`iv2-passo-*`) e a entrada dos
// itens da lista (`iv2-parte-*`) vêm do partial do módulo.
@import '@modules/aawz-intelligence-v2/design/efeitos.sass'

// Reset do <button>: sem borda, sem fundo, NUNCA (nem no hover) — é uma
// LINHA, não um card nem uma pílula (decisão do usuário, 14/09: a affordance
// é a varredura na tipografia, abaixo). O anel de foco fica no teclado.
.raciocinio-linha__botao
  max-width: 100%
  border: 0
  background: transparent
  color: inherit
  font: inherit
  cursor: default
  border-radius: var(--radius-sm)

  &--expansivel
    cursor: pointer

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px

// ★ A affordance do cabeçalho concluído (decisão do usuário, 14/09): ao
// passar o mouse (ou no foco de teclado), "Raciocínio da IA" escurece
// (n700 → n900, `--motion-duration-fast`) e "Raciocínio da IA · N etapas" recebe UM passe do
// gradiente da varredura — o mixin de `efeitos.sass` (keyframes únicas) com
// `$iv2-varredura-passe-unico` e iteração 1, não o loop do passo atual. No
// `__conteudo` (texto + auxiliar) para o "N etapas" brilhar junto. Só com
// `--concluido`: em streaming o `__texto` já tem a classe de estado, e dois
// brilhos se sobrepondo leem como falha. Sem fundo. O ícone ✦ já é
// `--primary` em repouso (`corDoIcone`), então não muda no hover.
.raciocinio-linha__botao--expansivel.raciocinio-linha__botao--concluido
  &:hover,
  &:focus-visible
    .raciocinio-linha__texto
      color: var(--neutral-900)

    .raciocinio-linha__conteudo
      @include iv2-varredura-de-texto($iv2-varredura-passe-unico, 1)

// A altura da caixa é a de UMA linha de 14px — é ela que impede a linha de
// encolher no instante entre o passo que sai e o que entra (`out-in`).
$altura-da-linha: 20px

.raciocinio-linha__caixa
  display: inline-block
  min-height: $altura-da-linha
  min-width: 0

.raciocinio-linha__texto
  display: inline-block
  line-height: $altura-da-linha
  color: var(--neutral-700)
  transition: color var(--motion-duration-fast, 120ms) var(--motion-easing-standard, ease-out)

  // ⚠️ [15/09/2026] A troca do passo "piscava": este `transition: color` (scoped,
  // declarado depois e mais específico) VENCIA o `transition: opacity, transform`
  // de `.iv2-passo-*-active` — a opacidade trocava seca e o Vue só esperava os
  // 120ms do `color`. Mesma armadilha do chip do índice de artefatos: a regra
  // com as DUAS classes ganha de qualquer `transition` própria do elemento.
  &.iv2-passo-enter-active,
  &.iv2-passo-leave-active
    transition: opacity $iv2-passo-duracao var(--motion-easing-standard, ease-out), transform $iv2-passo-duracao var(--motion-easing-standard, ease-out), color var(--motion-duration-fast, 120ms) var(--motion-easing-standard, ease-out)

  &--erro
    color: var(--danger-text)

.raciocinio-linha__conteudo
  min-width: 0

.raciocinio-linha__auxiliar
  white-space: nowrap
  font-variant-numeric: tabular-nums

// 1px para baixo: o flex centra o ícone nos 20px da linha (centro em 10px),
// mas o centro da x-height do texto de 14px fica em ~11,3px (meia-entrelinha
// 1,6 + ascendente 0,95em − 0,26em de x-height); o chevron lia 1–2px alto
// (print do usuário, 14/09). Não há utilitário `top-*` no Vuetify 2.0.11.
$ajuste-do-chevron: 1px

.raciocinio-linha__chevron
  position: relative
  top: $ajuste-do-chevron
  transition: transform var(--motion-duration-default, 200ms) var(--motion-easing-standard, ease-out)

  &--aberto
    transform: rotate(180deg)

// A lista expandida: indentada, com UM filete vertical à esquerda — sem card,
// sem hairlines horizontais (formato da referência). Coluna flex declarada —
// a regra global `.content-nav ul` impõe `display: flex` (em linha) e o chat
// vive dentro dela (mesmo cuidado do `next-steps`); o `az-gap-xsmall` do
// template é o respiro entre os passos.
// O recuo segue o ícone (`TAMANHO_PADRAO_DO_INDICADOR`, 22px) + o `mr-2` da
// linha: o filete sob o CENTRO do ícone (22 / 2), o texto sob o título
// (centro + 11 + 8). Números fechados: `/` no sass 1.64 é divisão deprecada.
$centro-do-icone: 11px
$recuo-do-texto: 30px

.raciocinio-linha__lista
  display: flex
  flex-direction: column
  list-style: none
  border-left: 1px solid var(--neutral-200)
  margin-left: $centro-do-icone
  padding-left: $recuo-do-texto - $centro-do-icone

// Verbo apagado × objeto escuro (print de referência: "Consultou" muted +
// `list_dashboards_bi` normal). Tokens direto, não `az-muted`: ele só compõe
// com `az-text-primary`, que força 16px, e a lista é 13px.
.raciocinio-linha__passo-verbo
  color: var(--neutral-600)

// `pre-wrap` só aqui: o racional da triage (frase livre, com quebras) mora
// inteiro no complemento; no `__passo-texto` ele preservaria o whitespace do
// template entre os dois spans.
.raciocinio-linha__passo-complemento
  color: var(--neutral-900)
  white-space: pre-wrap

// Sem movimento no SO: o hover é SÓ a troca de cor (o passe do gradiente sai;
// o `::after` fica parado fora da caixa) e o chevron gira sem transição.
@media (prefers-reduced-motion: reduce)
  .raciocinio-linha__botao--expansivel.raciocinio-linha__botao--concluido
    &:hover,
    &:focus-visible
      .raciocinio-linha__conteudo::after
        animation: none

  .raciocinio-linha__texto,
  .raciocinio-linha__texto.iv2-passo-enter-active,
  .raciocinio-linha__texto.iv2-passo-leave-active,
  .raciocinio-linha__chevron
    transition: none

// Só para o leitor de tela — mesmo `sr-only` do `agent-badge`.
//
// ⚠️ `user-select: none` NÃO é enfeite: a receita `sr-only` esconde do OLHO
// (1x1 recortado), mas o texto continua no fluxo — e entra na SELEÇÃO. Quem
// copia a resposta para colar em outro lugar leva "Pensando por 5 s" DUAS
// vezes, e lê isso como a tela repetindo o indicador. Medido em 19/09/2026,
// com o usuário colando o mesmo turno duplicado duas vezes seguidas.
.raciocinio-linha__anuncio
  position: absolute
  width: 1px
  height: 1px
  padding: 0
  margin: -1px
  overflow: hidden
  clip: rect(0, 0, 0, 0)
  white-space: nowrap
  border: 0
  user-select: none
</style>
